/**
 * Period-over-period change for a stat tile.
 *
 * Every stat that carries one is a growth metric — link scans, signups, new
 * accounts — so a rise is good and a fall is bad. A metric where the reverse
 * holds (expiries, overdue work) must not use this.
 *
 * Only stats whose prior window is exactly countable get a change at all. A
 * live balance (deposits held, catalog size) has no history to compare
 * against, and inventing one would read as measurement.
 */
export type StatChange = {
  current: number;
  previous: number;
  /** Reads after the badge, e.g. "vs prev 7 days". */
  window: string;
};

export type StatDelta = {
  text: string;
  variant: "good" | "bad" | "neutral";
};

/** Null when there is nothing to report — both windows empty. */
export function statDelta(current: number, previous: number): StatDelta | null {
  if (current === previous) {
    return previous === 0 ? null : { text: "0%", variant: "neutral" };
  }
  // No prior activity: a percentage has no base to divide by, so the count
  // itself is the honest figure.
  if (previous === 0) return { text: `+${current}`, variant: "good" };

  const pct = ((current - previous) / previous) * 100;
  const sign = pct > 0 ? "+" : "-";
  const rounded = Math.round(Math.abs(pct) * 10) / 10;
  return {
    // A change too small to survive rounding still moved — say so rather than
    // printing a flat "+0%" the badge is simultaneously coloring green.
    text: rounded === 0 ? `${sign}<0.1%` : `${sign}${rounded}%`,
    variant: pct > 0 ? "good" : "bad",
  };
}
